import {
  ILadderService,
  LadderPlayer,
  Event,
  LadderNews,
  Standing,
  StandingsFilter,
  Statement,
  FindPlayer,
} from './interfaces';
import { deserialize } from 'object-deserializer';
import {
  ladderNewsDeserializer,
  winnerDeserializer,
  rankingEventDeserializer,
  ladderPlayerDeserializer,
  statementEventDeserializer,
  findPlayerDeserializer,
} from './deserializers';
import { RangeValue } from '@ui-kit/components/rangePicker';
import { IApiClient } from '@base/features/api/model/apiClient';
import API_PATHS from '@base/features/api/constants/apiPaths';

export default class LadderService implements ILadderService {
  constructor(private apiClient: IApiClient, private GAME_ID: number) {}

  async getStatement(userId: string): Promise<Statement> {
    const res = await this.apiClient.authGet({ gameid: this.GAME_ID, json: 1 }, API_PATHS.statement);
    const data = await res.json();
    const events = deserialize(data, (o) =>
      o.required('statement').asArrayOfObjects(statementEventDeserializer)
    );
    const currentPoints = deserialize(data, (o) => o.required('points').asString);
    return { currentPoints, events };
  }

  async getStandings(filter: StandingsFilter, start?: number): Promise<Standing[]> {
    const res = await this.apiClient.authGet(
      { gid: this.GAME_ID, choice: filter, json: 1, start: start || 1 },
      API_PATHS.standings
    );
    const data = await res.json();
    return data.data;
  }

  async getLadderNews(): Promise<LadderNews[]> {
    const res = await this.apiClient.authPost({ json: 1, gid: this.GAME_ID }, API_PATHS.ladder);
    const data = await res.json();
    const news = deserialize(data, (o) => o.required('news').asArrayOfObjects(ladderNewsDeserializer));
    return news;
  }

  async getRankingReport(range?: RangeValue): Promise<Event[]> {
    const MAX_PLAYERS = 100;
    let params = {};
    if (range && !range.includes(null)) {
      const dateList = range.flatMap((date) => date && date.format('YYYY-MM-DD').split('-'));
      const keys = ['yyf', 'mmf', 'ddf', 'yyt', 'mmt', 'ddt'];
      params = keys.reduce((obj, key, index) => ({ ...obj, [key]: dateList[index] }), {});
    }
    const res = await this.apiClient.get(
      { ...params, gid: this.GAME_ID, rankR: MAX_PLAYERS, json: 1 },
      API_PATHS.ranked
    );

    const data = await res.json();
    return deserialize(data, (o) => o.required('table').asArrayOfObjects(rankingEventDeserializer));
  }

  async getTopPlayers(): Promise<LadderPlayer[]> {
    const res = await this.apiClient.authPost({ json: 1, gid: this.GAME_ID }, API_PATHS.ladder);
    const data = await res.json();
    const top1_10 = deserialize(data, (o) =>
      o.required('top1_10').asArrayOfObjects(ladderPlayerDeserializer)
    );
    const top11_20 = deserialize(data, (o) =>
      o.required('top11_20').asArrayOfObjects(ladderPlayerDeserializer)
    );
    return [...top1_10, ...top11_20];
  }

  async getLastMonthWinners(): Promise<LadderPlayer[]> {
    const res = await this.apiClient.authGet(
      { json: 1, gid: this.GAME_ID, choice: 'lastprizes' },
      API_PATHS.standings
    );
    const data = await res.json();
    return deserialize(data, (o) => o.required('data').asArrayOfObjects(winnerDeserializer));
  }

  async findPlayers(name: string): Promise<FindPlayer[]> {
    const res = await this.apiClient.authGet(
      { json: 1, gid: this.GAME_ID, name: name },
      API_PATHS.findPlayer
    );
    const data = await res.json();
    const findings = deserialize(data, (o) => o.required('Players').asArrayOfObjects(findPlayerDeserializer));
    return findings;
  }
}
